import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Send, Trash2, ListChecks, Check, X, AlertCircle, ShieldAlert, ExternalLink, KeyRound, Download } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { getApiUrl } from "@/utils/api";
import { IntegrationApiLogToggle } from "./IntegrationApiLogToggle";
import CopyButton from "@/components/admin/CopyButton";

const API = getApiUrl();
const MASK = "••••••••";

const CREDS_FIELDS = [
  { key: "network_id",          label: "Network ID",           placeholder: "e.g. 14285" },
  { key: "branch_id",           label: "Branch ID",            placeholder: "e.g. 300299" },
  { key: "channel",             label: "Channel",              placeholder: "Sales or Lettings" },
  { key: "basic_auth_user",     label: "Basic-auth username",  placeholder: "optional" },
  { key: "basic_auth_password", label: "Basic-auth password",  placeholder: "optional", secret: true },
  { key: "keystore_password",   label: "Keystore password",    placeholder: "for the Rightmove mTLS cert", secret: true },
  { key: "zip_password",        label: "Zip password",         placeholder: "used to unpack the cert bundle", secret: true },
];

export default function RightmoveTestingSection() {
  const [envConfig, setEnvConfig] = useState({ test: {}, live: {} });
  const [endpoints, setEndpoints] = useState({ test: {}, live: {} });
  const [activeEnv, setActiveEnv] = useState("test");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState("");
  // Manual override for the `agent_ref` sent on `remove`. When non-empty,
  // this wins over the property dropdown selection — useful for cleaning
  // stale UAT entries whose CMS record is already gone.
  const [manualAgentRef, setManualAgentRef] = useState("");
  const [busyAction, setBusyAction] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, propsResp] = await Promise.all([
          axios.get(`${API}/admin/integrations/rightmove-settings`, { withCredentials: true }),
          axios.get(`${API}/properties`),
        ]);
        const envs = data.environments || {};
        setEnvConfig({
          test: envs.test?.settings || {},
          live: envs.live?.settings || {},
        });
        setEndpoints({
          test: envs.test?.endpoints || {},
          live: envs.live?.endpoints || {},
        });
        setProperties(propsResp.data || []);
        if ((propsResp.data || []).length) setPropertyId(propsResp.data[0].property_id);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load Rightmove settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cfg = envConfig[activeEnv] || {};

  const patchField = (key, val) =>
    setEnvConfig((c) => ({ ...c, [activeEnv]: { ...(c[activeEnv] || {}), [key]: val } }));

  const saveSettings = async () => {
    setSaving(true);
    try {
      const body = { environment: activeEnv, ...cfg };
      // Skip masked placeholders (no change intended)
      Object.keys(body).forEach((k) => { if (body[k] === MASK) delete body[k]; });
      await axios.put(
        `${API}/admin/integrations/rightmove-settings`,
        body,
        { withCredentials: true }
      );
      toast.success(`Saved ${activeEnv.toUpperCase()} settings`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // For `remove`, allow a manually typed agent_ref to win over the dropdown
  // so admins can clean orphaned Rightmove listings whose CMS record no
  // longer exists.
  const effectivePropertyId = (action) => {
    if (action === "list") return undefined;
    if (action === "remove" && manualAgentRef.trim()) return manualAgentRef.trim();
    return propertyId || undefined;
  };

  const runAction = async (action, opts = {}) => {
    const { dry_run = true } = opts;
    setBusyAction(`${action}:${dry_run ? "dry" : "live"}`);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/rightmove-test-call`,
        {
          environment: activeEnv,
          action,
          property_id: effectivePropertyId(action),
          dry_run,
        },
        { withCredentials: true }
      );
      setLastResult(data);
    } catch (e) {
      setLastResult({ status: "error", detail: e?.response?.data?.detail || String(e) });
    } finally {
      setBusyAction(null);
    }
  };

  // Run a dry-run for the chosen action and trigger a download of the
  // resulting JSON payload so admins can inspect / save / share what would be
  // sent to Rightmove without hitting Rightmove.
  const downloadJson = async (action) => {
    setBusyAction(`${action}:json`);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/integrations/rightmove-test-call`,
        {
          environment: activeEnv,
          action,
          property_id: effectivePropertyId(action),
          dry_run: true,
        },
        { withCredentials: true }
      );
      setLastResult(data);
      // Prefer the request payload (`body`) when available (dry-run response),
      // otherwise fall back to the full envelope.
      const jsonToDownload = data.body ?? data;
      const blob = new Blob([JSON.stringify(jsonToDownload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, "-");
      const propSlug = effectivePropertyId(action) ? effectivePropertyId(action).slice(0, 12) : "no-property";
      const link = document.createElement("a");
      link.href = url;
      link.download = `rightmove_${action}_${activeEnv}_${propSlug}_${ts}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${action} JSON`);
    } catch (e) {
      const detail = e?.response?.data?.detail || String(e);
      setLastResult({ status: "error", detail });
      toast.error(`JSON generation failed: ${detail}`);
    } finally {
      setBusyAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-6">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Rightmove testing…
      </div>
    );
  }

  const isLive = activeEnv === "live";

  return (
    <div className="space-y-6" data-testid="rightmove-testing-section">
      <IntegrationApiLogToggle provider="rightmove" />
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-primary" /> Rightmove Testing
              </CardTitle>
              <CardDescription>
                Configure connection details per environment, dry-run to preview the exact payload that would be sent, then flip to a real call when Rightmove UAT is ready.
              </CardDescription>
            </div>
            <Badge className={isLive ? "bg-red-100 text-red-800 border-red-300" : "bg-green-100 text-green-800 border-green-300"}>
              {isLive ? "LIVE" : "TEST"} environment
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs value={activeEnv} onValueChange={setActiveEnv} className="w-full">
            <TabsList className="grid grid-cols-2 max-w-sm">
              <TabsTrigger value="test" data-testid="rm-env-test">Test (UAT)</TabsTrigger>
              <TabsTrigger value="live" data-testid="rm-env-live">
                Live (Production) <ShieldAlert className="ml-1 h-3 w-3 text-red-600" />
              </TabsTrigger>
            </TabsList>

            {["test", "live"].map((env) => (
              <TabsContent key={env} value={env} className="mt-5 space-y-5">
                {env === "test" && (
                  <div
                    className="p-3 border border-blue-300 bg-blue-50 rounded-md text-sm text-blue-900 space-y-2"
                    data-testid="rm-uat-preview-note"
                  >
                    <div className="flex items-start gap-2">
                      <ExternalLink className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Rightmove UAT preview portal</div>
                        <div className="text-blue-800 text-xs leading-relaxed mt-0.5">
                          After a successful <span className="font-mono">send</span>, the response returns a{" "}
                          <span className="font-mono">cms_id</span>. Open{" "}
                          <a
                            href="https://www.adftest.rightmove.com/properties/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline font-medium text-blue-800 hover:text-blue-900"
                            data-testid="rm-uat-preview-link"
                          >
                            www.adftest.rightmove.com/properties/&lt;id&gt;
                          </a>{" "}
                          in a new tab to preview the live UAT listing.
                        </div>
                      </div>
                    </div>
                    <div className="pl-6 text-xs text-amber-900 bg-amber-100 border border-amber-300 rounded p-2">
                      <span className="font-semibold">⏱ 15–40 minutes propagation.</span> Rightmove
                      processes new properties for up to ~40 minutes before they&apos;re visible on
                      the preview site. Hitting the URL sooner shows a &quot;we couldn&apos;t find
                      the property&quot; page — expected.
                    </div>
                    <div className="flex items-center gap-2 pl-6 text-xs text-blue-800">
                      <KeyRound className="h-3.5 w-3.5" />
                      <span>
                        Portal credentials — <span className="font-mono">Username:</span>{" "}
                        <span className="font-mono font-semibold">rightmove</span>
                        <span className="mx-2 text-blue-400">·</span>
                        <span className="font-mono">Password:</span>{" "}
                        <span className="font-mono font-semibold">rightpass</span>
                      </span>
                    </div>
                  </div>
                )}
                {isLive && env === "live" && (
                  <div className="flex items-start gap-2 p-3 border border-red-300 bg-red-50 text-red-900 rounded-md text-sm">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      Live actions publish to production Rightmove and are billable. Dry-run first.
                    </div>
                  </div>
                )}

                {/* Endpoints */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Endpoints ({env})</Label>
                  <div className="grid gap-2 text-sm">
                    {["send", "remove", "list"].map((k) => (
                      <div key={k} className="grid grid-cols-[100px_1fr] gap-2 items-center">
                        <span className="font-mono text-xs text-muted-foreground">{k}</span>
                        <code className="bg-muted px-2 py-1 rounded text-xs overflow-x-auto">
                          {(endpoints[env] || {})[k]}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Credentials */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Credentials ({env})</Label>
                  <div className="grid md:grid-cols-2 gap-3">
                    {CREDS_FIELDS.map((f) => (
                      <div key={f.key} className="space-y-1">
                        <Label className="text-xs">{f.label}</Label>
                        <Input
                          type={f.secret ? "password" : "text"}
                          placeholder={f.placeholder}
                          value={(envConfig[env] || {})[f.key] || ""}
                          onChange={(e) => {
                            if (env !== activeEnv) return;
                            patchField(f.key, e.target.value);
                          }}
                          data-testid={`rm-cred-${env}-${f.key}`}
                          disabled={env !== activeEnv}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={saveSettings}
                    disabled={saving || env !== activeEnv}
                    data-testid={`rm-save-${env}`}
                  >
                    {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                    Save {env.toUpperCase()} settings
                  </Button>
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Test Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Run a call</CardTitle>
          <CardDescription>
            Dry-run first to preview the payload; then send it for real. All calls target the <strong>{activeEnv.toUpperCase()}</strong> environment configured above.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
            <div className="space-y-1">
              <Label>Property (used for send / remove)</Label>
              <select
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full"
                data-testid="rm-property-select"
              >
                {properties.map((p) => (
                  <option key={p.property_id} value={p.property_id}>
                    {p.title} — {p.property_id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-3">
            {[
              { key: "send", label: "SendProperty", Icon: Send },
              { key: "remove", label: "RemoveProperty", Icon: Trash2 },
              { key: "list", label: "GetBranchPropertyList", Icon: ListChecks },
            ].map(({ key, label, Icon }) => (
              <div key={key} className="border rounded-md p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-primary" />
                  <span className="font-semibold text-sm">{label}</span>
                </div>
                {key === "remove" && (
                  <div className="space-y-1">
                    <label className="text-[11px] font-medium text-muted-foreground block">
                      Manual agent_ref (overrides dropdown)
                    </label>
                    <input
                      type="text"
                      value={manualAgentRef}
                      onChange={(e) => setManualAgentRef(e.target.value)}
                      placeholder="e.g. 6b8da0e8-…"
                      className="w-full h-8 rounded border border-input bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                      data-testid="rm-remove-manual-agent-ref"
                    />
                    {manualAgentRef.trim() && (
                      <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Using manual agent_ref — dropdown ignored
                      </p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => runAction(key, { dry_run: true })}
                    disabled={busyAction !== null}
                    data-testid={`rm-action-${key}-dry`}
                  >
                    {busyAction === `${key}:dry` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Dry-run
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadJson(key)}
                    disabled={busyAction !== null}
                    title="Generate the JSON payload we would send and download it"
                    data-testid={`rm-action-${key}-json`}
                  >
                    {busyAction === `${key}:json` ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3 mr-1" />
                    )}
                    Generate JSON
                  </Button>
                  <Button
                    size="sm"
                    variant={isLive ? "destructive" : "default"}
                    onClick={() => {
                      if (isLive && !window.confirm(`Send REAL '${label}' call to Rightmove Production?`)) return;
                      runAction(key, { dry_run: false });
                    }}
                    disabled={busyAction !== null}
                    data-testid={`rm-action-${key}-real`}
                  >
                    {busyAction === `${key}:live` ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Send
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {lastResult && (
            <div className="border rounded-md">
              <div className="px-3 py-2 border-b bg-muted/40 flex items-center gap-2">
                {lastResult.status === "dry-run" && <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-800">Dry-run</Badge>}
                {lastResult.status === "sent" && (
                  <Badge className={lastResult.http_status >= 400 ? "bg-red-100 text-red-800 border-red-300" : "bg-green-100 text-green-800 border-green-300"}>
                    HTTP {lastResult.http_status}
                  </Badge>
                )}
                {(lastResult.status === "error" || lastResult.status === "network-error") && (
                  <Badge className="bg-red-100 text-red-800 border-red-300 flex items-center gap-1">
                    <X className="h-3 w-3" /> {lastResult.status}
                  </Badge>
                )}
                {lastResult.endpoint && (
                  <code className="text-xs bg-muted px-2 py-0.5 rounded truncate">{lastResult.endpoint}</code>
                )}
              </div>
              <div className="flex items-center justify-end mb-1">
                <CopyButton text={JSON.stringify(lastResult, null, 2)} testId="rm-test-response-copy" />
              </div>
              <pre className="p-3 text-xs bg-slate-950 text-slate-50 overflow-x-auto max-h-96" data-testid="rm-test-response">
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
